import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Camera, Upload, MapPin, User, ChevronDown, ChevronUp, Plus, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONDITION_FIELDS = [
  { key: 'roof_condition', label: 'Roof' },
  { key: 'ridge_condition', label: 'Ridge' },
  { key: 'tile_condition', label: 'Tile' },
  { key: 'valley_condition', label: 'Valley' },
  { key: 'barge_condition', label: 'Barge' },
  { key: 'sheet_condition', label: 'Sheet' },
  { key: 'spouting_condition', label: 'Spouting' },
  { key: 'fastening_condition', label: 'Fastening' },
  { key: 'flashing_condition', label: 'Flashing' },
  { key: 'appraisal_condition', label: 'Overall' },
];

const COMPONENT_TYPES = ['sheet', 'tile', 'ridge', 'valley', 'barge', 'spouting', 'fastening', 'flashing', 'cleaning', 'recoat', 'aquaseal', 'safety', 'travel', 'urgent', 'misc', 'underlay'];

export default function MobileAppraisalPage() {
  const { api } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [conditions, setConditions] = useState({});
  const [photos, setPhotos] = useState([]);
  const [section, setSection] = useState('info');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addCompType, setAddCompType] = useState('');
  const [compForm, setCompForm] = useState({});
  const [dropdowns, setDropdowns] = useState({});
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/mobile/appraisal/${id}`);
      setData(d);
      setConditions({
        roof_condition: d.roof_condition || '', ridge_condition: d.ridge_condition || '',
        tile_condition: d.tile_condition || '', valley_condition: d.valley_condition || '',
        barge_condition: d.barge_condition || '', sheet_condition: d.sheet_condition || '',
        spouting_condition: d.spouting_condition || '', fastening_condition: d.fastening_condition || '',
        flashing_condition: d.flashing_condition || '', appraisal_condition: d.appraisal_condition || '',
        appraisal_comments: d.appraisal_comments || '', roof_requirements: d.roof_requirements || '',
      });
      setPhotos(d.photos || []);
      setDropdowns(d.dropdowns || {});
    } catch { navigate('/appraisals'); }
    setLoading(false);
  }, [api, id, navigate]);

  useEffect(() => { load(); }, [load]);

  const saveConditions = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put(`/mobile/appraisal/${id}/conditions`, conditions);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handlePhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('appraisal_id', id);
      fd.append('description', '');
      try { await api.post('/photos/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch (err) { console.error(err); }
    }
    setUploading(false);
    load();
    if (fileRef.current) fileRef.current.value = '';
  };

  const addComponent = async () => {
    if (!addCompType) return;
    try {
      await api.post(`/mobile/appraisal/${id}/quick-component`, { component_type: addCompType, ...compForm });
      setCompForm({});
      setAddCompType('');
      load();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  if (!data) return null;

  const client = data.client || {};
  const property = data.property || {};
  const sections = [
    { key: 'info', label: 'Info' },
    { key: 'conditions', label: 'Conditions' },
    { key: 'components', label: 'Components' },
    { key: 'photos', label: `Photos (${photos.length})` },
  ];

  return (
    <div data-testid="mobile-appraisal-page" className="min-h-screen bg-[#F9F9F8]">
      {/* Mobile Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-[#E2E2DF] px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(`/appraisals/${id}`)} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft size={18} /></button>
          <h1 className="text-sm font-bold text-slate-900 font-[Manrope] truncate mx-2">{data.quote_type_ref || 'Appraisal'}</h1>
          <button data-testid="mobile-save-button" onClick={saveConditions} disabled={saving} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] text-white'}`}>
            {saved ? <><CheckCircle size={12} /> Saved</> : <><Save size={12} /> Save</>}
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="sticky top-[52px] z-10 bg-white border-b border-[#E2E2DF] flex overflow-x-auto">
        {sections.map(s => (
          <button key={s.key} data-testid={`mobile-tab-${s.key}`} onClick={() => setSection(s.key)} className={`flex-shrink-0 px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${section === s.key ? 'border-[#D35400] text-[#D35400]' : 'border-transparent text-slate-500'}`}>{s.label}</button>
        ))}
      </div>

      <div className="p-4 pb-24 space-y-4">
        {/* Info Section */}
        {section === 'info' && (
          <div className="space-y-3">
            <div className="bg-white rounded-lg border border-[#E2E2DF] p-4">
              <div className="flex items-center gap-2 mb-2"><User size={16} className="text-[#D35400]" /><span className="text-xs font-bold uppercase tracking-wider text-slate-500">Client</span></div>
              <p className="text-base font-semibold text-slate-900">{client.name_first} {client.name_last}</p>
              {client.phone_mobile && <p className="text-sm text-slate-600 mt-1">{client.phone_mobile}</p>}
              {client.email && <p className="text-sm text-slate-600">{client.email}</p>}
            </div>
            <div className="bg-white rounded-lg border border-[#E2E2DF] p-4">
              <div className="flex items-center gap-2 mb-2"><MapPin size={16} className="text-[#4A6FA5]" /><span className="text-xs font-bold uppercase tracking-wider text-slate-500">Property</span></div>
              <p className="text-base font-semibold text-slate-900">{property.property_address_num} {property.property_address_street}</p>
              <p className="text-sm text-slate-600">{property.property_address_suburb}, {property.property_address_city}</p>
            </div>
            {(data.roof_details || []).length > 0 && (
              <div className="bg-white rounded-lg border border-[#E2E2DF] p-4">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Roof Details</span>
                {data.roof_details.map(r => (
                  <div key={r._id} className="grid grid-cols-2 gap-2 text-sm">
                    {[['Type', r.roof_type], ['Brand', r.roof_brand], ['Colour', r.roof_colour], ['Age', r.roof_age], ['State', r.roof_state], ['M2', r.roof_m2]].map(([l, v]) => (
                      <div key={l}><span className="text-xs text-slate-400">{l}</span><p className="font-medium text-slate-800">{v || '-'}</p></div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <div className="bg-white rounded-lg border border-[#E2E2DF] p-4">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Status</span>
              <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[#D35400]/10 text-[#D35400]">{data.appraisal_status}</span>
            </div>
          </div>
        )}

        {/* Conditions Section */}
        {section === 'conditions' && (
          <div className="space-y-3">
            {CONDITION_FIELDS.map(f => (
              <div key={f.key} className="bg-white rounded-lg border border-[#E2E2DF] p-3">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{f.label} Condition</label>
                <textarea data-testid={`mobile-${f.key}`} value={conditions[f.key] || ''} onChange={e => setConditions(prev => ({...prev, [f.key]: e.target.value}))} rows={2} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" placeholder={`Describe ${f.label.toLowerCase()} condition...`} />
              </div>
            ))}
            <div className="bg-white rounded-lg border border-[#E2E2DF] p-3">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Requirements</label>
              <textarea value={conditions.roof_requirements || ''} onChange={e => setConditions(prev => ({...prev, roof_requirements: e.target.value}))} rows={3} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" placeholder="Overall requirements..." />
            </div>
            <div className="bg-white rounded-lg border border-[#E2E2DF] p-3">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Comments</label>
              <textarea value={conditions.appraisal_comments || ''} onChange={e => setConditions(prev => ({...prev, appraisal_comments: e.target.value}))} rows={3} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" placeholder="Additional notes..." />
            </div>
          </div>
        )}

        {/* Components Section */}
        {section === 'components' && (
          <div className="space-y-3">
            <div className="bg-white rounded-lg border border-[#E2E2DF] p-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-800">Quick Add Component</span>
              </div>
              <select data-testid="mobile-component-type" value={addCompType} onChange={e => setAddCompType(e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm mb-2">
                <option value="">Select Type</option>
                {COMPONENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
              {addCompType && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-slate-400">Type</label>
                      <select value={compForm.item_type || ''} onChange={e => setCompForm({...compForm, item_type: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm">
                        <option value="">Select</option>
                        {(dropdowns[`${addCompType}_types`] || []).map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div><label className="text-[10px] text-slate-400">State</label>
                      <select value={compForm.state || ''} onChange={e => setCompForm({...compForm, state: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm">
                        <option value="">Select</option>
                        {(dropdowns.component_states || []).map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div><label className="text-[10px] text-slate-400">Qty</label><input type="number" value={compForm.quantity || ''} onChange={e => setCompForm({...compForm, quantity: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" /></div>
                    <div><label className="text-[10px] text-slate-400">Length</label><input type="number" value={compForm.length || ''} onChange={e => setCompForm({...compForm, length: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" /></div>
                  </div>
                  <div><label className="text-[10px] text-slate-400">Requirements</label><input value={compForm.requirements || ''} onChange={e => setCompForm({...compForm, requirements: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" /></div>
                  <button data-testid="mobile-add-component-button" onClick={addComponent} className="w-full py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold flex items-center justify-center gap-1"><Plus size={14} /> Add Component</button>
                </div>
              )}
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Existing ({(data.components || []).length})</div>
            {(data.components || []).map(c => (
              <div key={c._id} className="bg-white rounded-lg border border-[#E2E2DF] p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#D35400] uppercase">{c.component_type}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.state === 'Good' ? 'bg-green-50 text-green-700' : c.state === 'Poor' || c.state === 'Very Poor' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{c.state || '-'}</span>
                </div>
                <p className="text-sm text-slate-800 mt-1">{c.item_type || '-'} {c.quantity ? `x${c.quantity}` : ''} {c.length ? `${c.length}m` : ''}</p>
                {c.requirements && <p className="text-xs text-slate-500 mt-1">{c.requirements}</p>}
              </div>
            ))}
            {(data.components || []).length === 0 && <p className="text-center py-6 text-sm text-slate-400">No components added yet</p>}
          </div>
        )}

        {/* Photos Section */}
        {section === 'photos' && (
          <div className="space-y-3">
            <label className={`flex items-center justify-center gap-2 w-full py-4 bg-white border-2 border-dashed border-[#D35400] rounded-lg text-[#D35400] font-semibold text-sm cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
              <Camera size={20} /> {uploading ? 'Uploading...' : 'Take Photo / Upload'}
              <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={handlePhoto} className="hidden" disabled={uploading} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              {photos.map(p => (
                <div key={p._id} className="rounded-lg overflow-hidden border border-[#E2E2DF]">
                  <div className="aspect-square"><img src={`${BACKEND_URL}${p.url}`} alt={p.description} className="w-full h-full object-cover" loading="lazy" /></div>
                  {p.description && <p className="text-[10px] text-slate-500 p-1.5 truncate">{p.description}</p>}
                </div>
              ))}
            </div>
            {photos.length === 0 && <p className="text-center py-8 text-sm text-slate-400">No photos yet. Tap above to take photos.</p>}
          </div>
        )}
      </div>

      {/* Bottom Save Bar (sticky on mobile) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E2DF] px-4 py-3 safe-area-bottom lg:hidden z-20">
        <button data-testid="mobile-bottom-save" onClick={saveConditions} disabled={saving} className={`w-full py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] text-white active:bg-[#A04000]'}`}>
          {saved ? <><CheckCircle size={16} /> Saved Successfully</> : <><Save size={16} /> Save All Changes</>}
        </button>
      </div>
    </div>
  );
}
